import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ReactNode } from "react";
import { DropdownMenu, type EvaluatedItemProps } from "@repo/ui";
import { cn } from "@repo/utils";
import type { TableColumn } from "./workflows-table";

export const WorkflowsTableRow = <T extends object>({
  row,
  index,
  columns,
  selectedRowIndex,
  handleRowClick,
  dropdownMenuItems,
  menuPlacement,
}: {
  row: T & { id: string | number };
  index: number;
  columns: TableColumn<T>[];
  selectedRowIndex: number | null;
  handleRowClick: (index: number, e: React.MouseEvent<HTMLTableRowElement>) => void;
  dropdownMenuItems?: EvaluatedItemProps[];
  menuPlacement: "top" | "bottom";
}) => {
  const isSelected = selectedRowIndex === index;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 50 : "auto",
    position: isDragging ? "relative" : undefined,
  } as React.CSSProperties;

  return (
    <tr
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn("relative cursor-pointer border-b border-b-gray-300 last:border-0", {
        "z-10": isSelected,
        "bg-gray-50 opacity-80 shadow-md": isDragging,
      })}
      onClick={(e) => handleRowClick(index, e)}
    >
      {columns.map((column, colIndex) => {
        const isFirstColumn = colIndex === 0;
        const isLastColumn = colIndex === columns.length - 1;

        return (
          <td
            key={String(column.key)}
            className={cn("py-4", {
              "bg-blue-700 font-medium text-white": isSelected && !isDragging,
              "relative before:absolute before:top-0 before:right-full before:h-full before:w-2 before:rounded-l-sm before:bg-blue-700 before:content-['']":
                isSelected && isFirstColumn && !isDragging,
              "relative after:absolute after:top-0 after:left-full after:h-full after:w-2 after:rounded-r-sm after:bg-blue-700 after:content-['']":
                isSelected && isLastColumn && !isDragging,
            })}
          >
            {column.render ? column.render(row[column.key], row) : (row[column.key] as ReactNode)}

            {isLastColumn && !!dropdownMenuItems?.length && (
              <DropdownMenu
                placement={menuPlacement}
                align="right"
                className={cn(
                  "absolute top-1/2 right-1 z-10 h-5 w-5 -translate-y-1/2",
                  "opacity-0",
                  {
                    "opacity-100": isSelected && !isDragging,
                  }
                )}
                iconClassName="w-5 h-5 shrink-0 p-1 rounded-full text-black"
                items={dropdownMenuItems}
              />
            )}
          </td>
        );
      })}
    </tr>
  );
};
